<script setup lang="ts">
import type { ChartData } from '~/composables/useChartData';
import { useQueryClient } from '@tanstack/vue-query';
import { useChartData } from '~/composables/useChartData';
import pastasAcordosNoMes from '~/graphql/pre-defined-filters/pastasAcordosNoMes';
import pastasAtivas from '~/graphql/pre-defined-filters/pastasAtivas';
import pastasCadastradasNoMes from '~/graphql/pre-defined-filters/pastasCadastradasNoMes';
import pastasEncerradasNoMes from '~/graphql/pre-defined-filters/pastasEncerradasNoMes';
import pastasGcpjDesatualizado from '~/graphql/pre-defined-filters/pastasGcpjDesatualizado';
import publicacoesHoje from '~/graphql/pre-defined-filters/publicacoesHoje';
import { formatDinheiro } from '~/shared/utils/helpers';
import { useDatatableDrawerStore } from '~/stores/datatableStore';

interface Data {
  key: string;
  value: any;
}

interface Props {
  data: Data[];
}

const props = defineProps<Props>();

const datatableDrawerStore = useDatatableDrawerStore();

const totalPastasAtivasAgrupadosPorGerencia = useChartData(
  props.data.find(
    (x) => x.key === 'visaoGeral-totalPastasAtivasAgrupadosPorGerencia',
  )?.value,
  ([_, valA], [__, valB]) => {
    return valB - valA;
  },
  'Total de pastas',
);

const totalPastasAtivasCarteiraNaoVeiculoAgrupadosPorResponsavelNegocial =
  useChartData(
    props.data.find(
      (x) =>
        x.key ===
        'visaoGeral-totalPastasAtivasCarteiraNaoVeiculoAgrupadosPorResponsavelNegocial',
    )?.value,
    ([_, valA], [__, valB]) => {
      return valB - valA;
    },
    'Total de pastas',
  );

const totalPastasAtivasCarteiraVeiculoAgrupadosPorResponsavelNegocial =
  useChartData(
    props.data.find(
      (x) =>
        x.key ===
        'visaoGeral-totalPastasAtivasCarteiraVeiculoAgrupadosPorResponsavelNegocial',
    )?.value,
    ([_, valA], [__, valB]) => {
      return valB - valA;
    },
    'Total de pastas',
  );

const totalPastasAtivasSemCarteiraAgrupadosPorResponsavelNegocial =
  useChartData(
    props.data.find(
      (x) =>
        x.key ===
        'visaoGeral-totalPastasAtivasSemCarteiraAgrupadosPorResponsavelNegocial',
    )?.value,
    ([_, valA], [__, valB]) => {
      return valB - valA;
    },
    'Total de pastas',
  );

const totalPastasAtivasGgpAgrupadosPorResponsavel = useChartData(
  props.data.find(
    (x) => x.key === 'visaoGeral-totalPastasAtivasGgpAgrupadosPorResponsavel',
  )?.value,
  ([_, valA], [__, valB]) => {
    return valB - valA;
  },
  'Total de pastas',
);

const totalPastasAtivasAgrupadosPorResponsavelProcessual = useChartData(
  props.data.find(
    (x) =>
      x.key === 'visaoGeral-totalPastasAtivasAgrupadosPorResponsavelProcessual',
  )?.value,
  ([_, valA], [__, valB]) => {
    return valB - valA;
  },
  'Total de pastas',
);

const totalPastasAtivasAgrupadosPorResponsavelNegocial = useChartData(
  props.data.find(
    (x) =>
      x.key === 'visaoGeral-totalPastasAtivasAgrupadosPorResponsavelNegocial',
  )?.value,
  ([_, valA], [__, valB]) => {
    return valB - valA;
  },
  'Total de pastas',
);

const gerentesNegociais =
  (props.data.find(
    (x) =>
      x.key ===
      'visaoGeral-totalPastasAtivasAgrupadosPorResponsavelNegocialEAgrupadosPorGerenteNegocial',
  )?.value as Record<string, Record<string, number>>) ??
  ({} as Record<string, Record<string, number>>);

const gerentesNegociaisChartData: Record<string, ChartData> = {};

Object.entries(gerentesNegociais).forEach(([key, value]) => {
  gerentesNegociaisChartData[key] = useChartData(
    value,
    ([_, valA], [__, valB]) => {
      return valB - valA;
    },
    'Total de pastas',
  ).value;
});

const totalPastasAtivasUltimosMeses = useChartData(
  props.data.find((x) => x.key === 'visaoGeral-totalPastasAtivasUltimosMeses')
    ?.value,
  ([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  },
  'Total de pastas',
  (key, _) => key.split('|')[0],
);

const totalValoresCausasUltimosMeses = useChartData(
  props.data.find((x) => x.key === 'visaoGeral-totalValoresCausasUltimosMeses')
    ?.value,
  ([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  },
  'Valores das causas',
  (key, _) => key.split('|')[0],
);

const totalAcordosUltimosMeses = useChartData(
  props.data.find((x) => x.key === 'visaoGeral-totalAcordosUltimosMeses')
    ?.value,
  ([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  },
  'Valores dos acordos',
  (key, _) => key.split('|')[0],
);

const totalPublicacoesUltimosDias = useChartData(
  props.data.find((x) => x.key === 'visaoGeral-totalPublicacoesUltimosDias')
    ?.value,
  ([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  },
  'Total de publicações',
  (key, _) => key.split('|')[0],
);

const totalPastasAgrupadasPorStatus = useChartData(
  props.data.find((x) => x.key === 'visaoGeral-totalPastasAgrupadasPorStatus')
    ?.value,
  ([_, valA], [__, valB]) => {
    return valB - valA;
  },
  'Total de pastas',
);

const totalPastasAptasParaApreensaoAgrupadasPorRegional = useChartData(
  props.data.find(
    (x) =>
      x.key === 'visaoGeral-totalPastasAptasParaApreensaoAgrupadasPorRegional',
  )?.value,
  ([_, valA], [__, valB]) => {
    return valB - valA;
  },
  'Total de pastas',
);

const totalPastasAcordosNoMesAgrupadosPorResponsavel = useChartData(
  props.data.find(
    (x) =>
      x.key === 'visaoGeral-totalPastasAcordosNoMesAgrupadosPorResponsavel',
  )?.value,
  ([_, valA], [__, valB]) => {
    return valB - valA;
  },
  'Total de pastas',
);

const queryClient = useQueryClient();

function onDatatableDrawerClose() {
  queryClient.invalidateQueries({
    queryKey: ['visao-geral'],
    exact: true,
  });
}
</script>
<template>
  <a-row :gutter="24" style="margin-top: 8px">
    <a-col :span="4">
      <Stats
        title="PASTAS ATIVAS"
        :clickable="true"
        :value="
          props.data.find((x) => x.key === 'visaoGeral-totalPastasAtivas')
            ?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'PastaGqlDatatable',
              title: 'PASTAS ATIVAS',
              variables: pastasAtivas(),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
    <a-col :span="4">
      <Stats
        title="GCPJ DESATUALIZADO"
        :clickable="true"
        :value="
          props.data.find(
            (x) => x.key === 'visaoGeral-totalPastasGcpjsDesatualizados',
          )?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'PastaGqlDatatable',
              title: 'PASTAS COM GCPJ DESATUALIZADO',
              variables: pastasGcpjDesatualizado(),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
    <a-col :span="4">
      <Stats
        title="ACORDOS NO MÊS"
        :clickable="true"
        :value="
          props.data.find((x) => x.key === 'visaoGeral-totalAcordosNoMes')
            ?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'PastaGqlDatatable',
              title: 'PASTAS COM ACORDO NO MÊS',
              variables: pastasAcordosNoMes(),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
    <a-col :span="4">
      <Stats
        title="PASTAS CADASTRADAS NO MÊS"
        :clickable="true"
        :value="
          props.data.find(
            (x) => x.key === 'visaoGeral-totalPastasCadastradasNoMes',
          )?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'PastaGqlDatatable',
              title: 'PASTAS CADASTRADAS NO MÊS',
              variables: pastasCadastradasNoMes(),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
    <a-col :span="4">
      <Stats
        title="PASTAS ENCERRADAS NO MÊS"
        :clickable="true"
        :value="
          props.data.find(
            (x) => x.key === 'visaoGeral-totalPastasEncerradasNoMes',
          )?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'PastaGqlDatatable',
              title: 'PASTAS ENCERRADAS NO MÊS',
              variables: pastasEncerradasNoMes(),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
    <a-col :span="4">
      <Stats
        title="PUBLICAÇÕES HOJE"
        :clickable="true"
        :value="
          props.data.find((x) => x.key === 'visaoGeral-totalPublicacoesHoje')
            ?.value
        "
        @click="
          datatableDrawerStore.open({
            componentName: 'PublicacaoNumeroProcessoGqlDatatable',
            title: 'PUBLICAÇÕES HOJE',
            variables: publicacoesHoje(),
            onClose: onDatatableDrawerClose,
          })
        "
      ></Stats>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="24">
      <BarChart
        title="PASTAS ATIVAS POR GERÊNCIA"
        :height="400"
        :categories="totalPastasAtivasAgrupadosPorGerencia.categories"
        :series="totalPastasAtivasAgrupadosPorGerencia.series"
      ></BarChart>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="24">
      <BarChart
        title="PASTAS ATIVAS (CARTEIRAS NÃO VEÍCULOS) - RESPONSÁVEIS NEGOCIAIS"
        :height="400"
        :categories="
          totalPastasAtivasCarteiraNaoVeiculoAgrupadosPorResponsavelNegocial.categories
        "
        :series="
          totalPastasAtivasCarteiraNaoVeiculoAgrupadosPorResponsavelNegocial.series
        "
      ></BarChart>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="24">
      <BarChart
        title="PASTAS ATIVAS (CARTEIRAS VEÍCULOS) - RESPONSÁVEIS NEGOCIAIS"
        :height="400"
        :categories="
          totalPastasAtivasCarteiraVeiculoAgrupadosPorResponsavelNegocial.categories
        "
        :series="
          totalPastasAtivasCarteiraVeiculoAgrupadosPorResponsavelNegocial.series
        "
      ></BarChart>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="24">
      <BarChart
        title="PASTAS ATIVAS (SEM CARTEIRA) - RESPONSÁVEIS NEGOCIAIS"
        :height="400"
        :categories="
          totalPastasAtivasSemCarteiraAgrupadosPorResponsavelNegocial.categories
        "
        :series="
          totalPastasAtivasSemCarteiraAgrupadosPorResponsavelNegocial.series
        "
      ></BarChart>
    </a-col>
  </a-row>

  <a-row
    v-for="[key, value] in Object.entries(gerentesNegociaisChartData).sort(
      ([keyA, _], [keyB, __]) => keyA.localeCompare(keyB),
    )"
    :key="key"
    :gutter="24"
    style="margin-top: 24px"
  >
    <a-col :span="24">
      <BarChart
        :title="`${key} / PASTAS ATIVAS`"
        :height="400"
        :categories="value.categories"
        :series="value.series"
      ></BarChart>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="24">
      <BarChart
        title="GGP - PASTAS ATIVAS"
        :height="400"
        :categories="totalPastasAtivasGgpAgrupadosPorResponsavel.categories"
        :series="totalPastasAtivasGgpAgrupadosPorResponsavel.series"
      ></BarChart>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="24">
      <BarChart
        title="PASTAS ATIVAS POR RESPONSÁVEL PROCESSUAL"
        :height="400"
        :categories="
          totalPastasAtivasAgrupadosPorResponsavelProcessual.categories
        "
        :series="totalPastasAtivasAgrupadosPorResponsavelProcessual.series"
      ></BarChart>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="24">
      <BarChart
        title="PASTAS ATIVAS POR RESPONSÁVEL NEGOCIAL"
        :height="400"
        series-name="Total de pastas"
        :categories="
          totalPastasAtivasAgrupadosPorResponsavelNegocial.categories
        "
        :series="totalPastasAtivasAgrupadosPorResponsavelNegocial.series"
      ></BarChart>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="12">
      <LineChart
        title="PASTAS ATIVAS"
        :categories="totalPastasAtivasUltimosMeses.categories"
        :series="totalPastasAtivasUltimosMeses.series"
      ></LineChart>
    </a-col>
    <a-col :span="12">
      <LineChart
        title="VALORES DAS CAUSAS"
        :categories="totalValoresCausasUltimosMeses.categories"
        :series="totalValoresCausasUltimosMeses.series"
        :value-formater="(value: number) => formatDinheiro(value)"
      ></LineChart>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="12">
      <Stats
        title="VALORES CAUSAS"
        :value="
          formatDinheiro(
            props.data.find((x) => x.key === 'visaoGeral-totalValoresCausas')
              ?.value,
          )
        "
      ></Stats>
    </a-col>
    <a-col :span="12">
      <Stats
        title="VALOR DE ACORDOS NO MÊS"
        :value="
          formatDinheiro(
            props.data.find(
              (x) => x.key === 'visaoGeral-totalValoresAcordosNoMes',
            )?.value,
          )
        "
      ></Stats>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="12">
      <LineChart
        title="TOTAL ACORDOS"
        :categories="totalAcordosUltimosMeses.categories"
        :series="totalAcordosUltimosMeses.series"
      ></LineChart>
    </a-col>
    <a-col :span="12">
      <LineChart
        title="PUBLICAÇÕES"
        :categories="totalPublicacoesUltimosDias.categories"
        :series="totalPublicacoesUltimosDias.series"
      ></LineChart>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="12">
      <BarChart
        title="PASTAS POR STATUS"
        :height="400"
        :categories="totalPastasAgrupadasPorStatus.categories"
        :series="totalPastasAgrupadasPorStatus.series"
      ></BarChart>
    </a-col>
    <a-col :span="12">
      <BarChart
        title="PASTAS APTAS PARA APREENSÃO POR REGIONAL"
        :height="400"
        :categories="
          totalPastasAptasParaApreensaoAgrupadasPorRegional.categories
        "
        :series="totalPastasAptasParaApreensaoAgrupadasPorRegional.series"
      ></BarChart>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="12">
      <BarChart
        title="ACORDOS NO MÊS POR RESPONSÁVEL"
        :height="400"
        :categories="totalPastasAcordosNoMesAgrupadosPorResponsavel.categories"
        :series="totalPastasAcordosNoMesAgrupadosPorResponsavel.series"
      ></BarChart>
    </a-col>
  </a-row>
</template>
